import { SoilSightKeyInsight } from "models";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { SoilSightOutcomesByScenario } from "pages/app/scenario-analysis/utils/forecast";
import { bandAvg } from "pages/app/scenario-analysis/utils/minAvgMax";

/**
 * A scenario is considered to have a "completed projection" when the backend has
 * returned at least one projected year (year > 0). Year 0 represents the
 * starting state and does not count toward a completed forecast.
 */
export const hasCompletedProjection = (scenarioId: string, soilSightOutcomes: SoilSightOutcomesByScenario | null): boolean =>
  (soilSightOutcomes?.[scenarioId] ?? []).some((singleYearOutcome) => singleYearOutcome.year > 0);

/**
 * Subset of scenarios that the user can drill into from the Key Insights modal.
 */
export const getScenariosWithProjections = (allScenarios: ScenarioData[], soilSightOutcomes: SoilSightOutcomesByScenario | null): ScenarioData[] =>
  allScenarios.filter((oneScenario) => hasCompletedProjection(oneScenario.id, soilSightOutcomes));

/**
 * Sum of the per-year contribution margins for a single scenario, restricted to
 * the visible projection window (years 1..projectionYears). This is what the
 * "Best scenario" card in the modal displays, even though the backend selects
 * the best scenario by *average* €/ha — see plan note on divergence.
 */
export const getScenarioCumulativeCm = (scenarioId: string, soilSightOutcomes: SoilSightOutcomesByScenario | null, projectionYears: number): number =>
  (soilSightOutcomes?.[scenarioId] ?? [])
    .filter((oneYearOutcome) => oneYearOutcome.year >= 1 && oneYearOutcome.year <= projectionYears)
    .reduce((cumulativeMargin, oneYearOutcome) => cumulativeMargin + bandAvg(oneYearOutcome.contributionMargin.total), 0);

/**
 * Guards against the scenario referenced by `keyInsight.bestScenarioId` being
 * deleted between calculation and modal open. Returns null when the cached
 * key insight no longer matches any current scenario.
 */
export const reconcileKeyInsight = (keyInsight: SoilSightKeyInsight | null, allScenarios: ScenarioData[]): SoilSightKeyInsight | null => {
  if (!keyInsight) return null;
  const bestStillExists = allScenarios.some((oneScenario) => oneScenario.id === keyInsight.bestScenarioId);
  return bestStillExists ? keyInsight : null;
};
